<template>
  <div class="rebook-calendar-search">
    <div class="rebook-calendar-search__date">
      <h3 class="rebook-calendar-search__date-text">{{ formattedDate }}</h3>
      <div class="rebook-calendar-search__date-line"></div>
    </div>
    <div class="rebook-calendar-search__buttons">
      <RebookSearchButton
        :button-text="$t('common.actions.navigate.previousDay')"
        :disabled="disabled || isCurrentDateToday"
        :booking-number="bookingNumber"
        :items-to-replace="itemsToReplace"
        :direction="direction"
        class="rebook-calendar-search__button rebook-calendar-search__button--prev"
        :is-calendar="true"
        @click.prevent="handlePrevDay"
      >
        <template #icon>
          <svg class="leading__chevron">
            <use xlink:href="#chevron-left" />
          </svg>
        </template>
      </RebookSearchButton>
      <RebookSearchButton
        :is-calendar="true"
        :button-text="$t('common.actions.navigate.nextDay')"
        :disabled="disabled"
        :booking-number="bookingNumber"
        :items-to-replace="itemsToReplace"
        :direction="direction"
        class="rebook-calendar-search__button rebook-calendar-search__button--next"
        @click.prevent="handleNextDay"
      >
        <template #icon>
          <svg class="trailing__chevron">
            <use xlink:href="#chevron-right" />
          </svg>
        </template>
      </RebookSearchButton>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, PropType } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { format, parseISO, isToday, addDays, subDays } from 'date-fns'
  import { nb } from 'date-fns/locale'
  import RebookSearchButton from './RebookSearchButton.vue'

  export default defineComponent({
    name: 'RebookCalendarSearch',
    components: {
      RebookSearchButton,
    },
    props: {
      showOutbound: {
        type: Boolean,
        required: true,
      },
      initialDate: {
        type: String,
        required: false,
        default: undefined,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      direction: {
        type: String as PropType<'outbound' | 'inbound' | 'entire'>,
        required: true,
      },
      bookingNumber: {
        type: String,
        required: true,
      },
      itemsToReplace: {
        type: Array as PropType<string[]>,
        required: true,
      },
    },
    setup(props) {
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()

      // Check if we have a return date in session storage for inbound rebooking
      if (!props.showOutbound && !journeyStore.returnDate) {
        const savedReturnDate = sessionStorage.getItem('rebooking_return_date')
        if (savedReturnDate) {
          journeyStore.setReturnDate(savedReturnDate)
        }
      }
      
      if (props.initialDate) {
        if (props.showOutbound) {
          journeyStore.setDepartureDate(props.initialDate)
        } else {
          journeyStore.setReturnDate(props.initialDate)
        }
      }

      const currentDate = computed(() => {
        let date = props.showOutbound
          ? journeyStore.departureDate
          : journeyStore.returnDate
        
        // If we're showing inbound (return) date and it's empty, try to get it from session storage
        if (!props.showOutbound && !date) {
          const savedReturnDate = sessionStorage.getItem('rebooking_return_date')
          if (savedReturnDate) {
            journeyStore.setReturnDate(savedReturnDate)
            date = savedReturnDate
          }
        }
        
        return date
      })

      const formattedDate = computed(() => {
        if (!currentDate.value) {
          console.error(
            '[DEBUG] RebookCalendarSearch - currentDate.value is empty or invalid:',
            currentDate.value
          )
          return 'Invalid date'
        }

        try {
          const date = parseISO(currentDate.value)
          const formatted = format(date, 'eeee dd. MMMM yyyy', { locale: nb })
          return formatted.toLowerCase()
        } catch (error) {
          console.error(
            '[DEBUG] RebookCalendarSearch - Error formatting date:',
            error,
            'Value:',
            currentDate.value
          )
          return 'Error formatting date'
        }
      })

      const isCurrentDateToday = computed(() => {
        const date = parseISO(currentDate.value)
        return isToday(date)
      })

      const handlePrevDay = (event: Event) => {
        if (event) {
          event.preventDefault()
        }
        const date = parseISO(currentDate.value)
        const newDate = subDays(date, 1)

        if (props.showOutbound) {
          journeyStore.setDepartureDate(format(newDate, 'yyyy-MM-dd'))
        } else {
          journeyStore.setReturnDate(format(newDate, 'yyyy-MM-dd'))
        }
      }

      const handleNextDay = (event: Event) => {
        if (event) {
          event.preventDefault()
        }
        const date = parseISO(currentDate.value)
        const newDate = addDays(date, 1)

        if (props.showOutbound) {
          journeyStore.setDepartureDate(format(newDate, 'yyyy-MM-dd'))
        } else {
          journeyStore.setReturnDate(format(newDate, 'yyyy-MM-dd'))
        }
      }

      return {
        formattedDate,
        isCurrentDateToday,
        handlePrevDay,
        handleNextDay,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .rebook-calendar-search {
    &__date {
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 0;

      &-text {
        @include title-medium;
        color: $clr-primary;
        white-space: nowrap;
      }

      &-line {
        width: 100%;
        border-bottom: 1px solid $clr-primary-100;
      }
    }

    &__buttons {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
  }

  .leading__chevron,
  .trailing__chevron {
    position: relative;
    top: 1px;
    width: 16px;
    height: 16px;
  }

  .leading__chevron {
    transform: rotate(-90deg);
  }

  .trailing__chevron {
    transform: rotate(90deg);
  }
</style>
